import './index.css'
import { PHONE } from '@disco/constants/DeviceTypes'
import { VIDEO_FILE } from '@disco/constants/FileTypes'
import {
  getPlaybackPaneForMediaPlayback,
  getPlayingPlaylist,
  getPlayingShortlist,
  getTrackPlaying,
} from '@disco/state-management/selectors/media'
import { getTrackTextSettings } from '@disco/state-management/selectors/user'
import { getSaveToDiscoVisibility } from '@disco/utils/business-metadata'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { sanitize } from '@disco/utils/string-utils'
import cn from 'clsx'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import MediaQuery from 'react-responsive'
import type { BusinessData } from 'types/business'
import type { ExplorerPane } from 'types/explorer'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { PublicPlaylist } from 'types/public-playlist'
import type { Shortlist } from 'types/shortlist'
import type { Track } from 'types/track'
import type { EventSource, TrackLocation } from 'types/ui'
import { ThemeContext, ThemeProvider } from 'contexts/theme'
import Waveform from 'components/common/player/waveform'
import TrackArtwork from 'components/common/tracks/track-artwork'
import { Box, Flex as RadixFlex } from 'components/ds'
import type { TrackActionsType } from 'components/track-actions'
import Button from 'components/ui/button'
import Flex from 'components/ui/flex'
import Logo from 'components/ui/logo'
import PlayTime from 'components/ui/play-time'
import Stack from 'components/ui/stack'
import getTrackStatus from 'components/utils/track-status'
import TrackDataInOneModal from 'pages/public-playlist/track-item/track-data-in-one-modal'
import TrackItemOptions from 'pages/public-playlist/track-item/track-item-options'
import PlaybackTime from './PlaybackTime'
import PlayerArtwork from './player-artwork'
import PlayerNext from './player-next'
import PlayerPane from './player-pane'
import PlayerPlay from './player-play'
import PlayerPrev from './player-prev'
import PlayerQueue from './player-queue'
import PlayerVolume from './player-volume'
import PlayerPlaybackManager from './PlayerPlaybackManager'
import ScrollableWaveform from './scrollable-waveform'

const stopPropagation = (event: React.SyntheticEvent<any>) =>
  event.stopPropagation()

const renderLyrics = (lyrics?: string) => {
  if (!lyrics) return null
  return lyrics
    .split('\n')
    .map((line: string, index: number) =>
      line ? <div key={index}>{line}</div> : <br key={index} />,
    )
}

type TrackOptions = {
  businessData: BusinessData
  playlist: PublicPlaylist
  metadataSubset: string[] | null | undefined
}
type Props = {
  campaignId?: number | null | undefined
  disabledQueue?: boolean
  isPublic?: boolean
  location?: TrackLocation
  mediaSource: EventSource
  modalStyle?: 'default' | 'overlay'
  recipientId?: number | null | undefined
  shareId?: number | null | undefined
  TrackActionsComponent?: TrackActionsType
  // TODO: Rename to something more specific
  whitelabelFooter?: string
  trackItemOptions?: TrackOptions
  disableAutoplay?: boolean
}

const ButtonsWrapper = ({ children }) => (
  <RadixFlex align="center" gap="3">
    {children}
  </RadixFlex>
)

/**
 * Main player component with playback controls, waveform visualization, and expandable interface
 * Supports both mobile and desktop layouts with lyrics, track actions, and queue management
 * @param campaignId - Campaign identifier for tracking
 * @param disabledQueue - Whether to disable the queue functionality
 * @param isPublic - Whether this is a public player instance
 * @param location - Track location context
 * @param mediaSource - Source of the media being played
 * @param modalStyle - Display style for video playback modals
 * @param recipientId - Recipient identifier for sharing contexts
 * @param shareId - Share identifier for tracking
 * @param TrackActionsComponent - Component for track-specific actions
 * @param whitelabelFooter - Custom footer content for whitelabel instances
 * @param trackItemOptions - Configuration for track item display options
 * @param disableAutoplay - Whether to disable automatic playback
 */
const Player = ({
  campaignId,
  disabledQueue,
  isPublic,
  location,
  mediaSource,
  modalStyle,
  recipientId,
  shareId,
  TrackActionsComponent,
  whitelabelFooter,
  trackItemOptions,
  disableAutoplay,
}: Props) => {
  const currentTrack: Track | null | undefined = useSelector(getTrackPlaying)
  const groupId: number | null | undefined = useSelector(
    state => state.media.trackGroupId,
  )
  const playbackPane: ExplorerPane | null | undefined = useSelector(
    getPlaybackPaneForMediaPlayback,
  )
  const trackPlaylist: Playlist | null | undefined =
    useSelector(getPlayingPlaylist)
  const trackShortlist: Shortlist | null | undefined =
    useSelector(getPlayingShortlist)
  const textSettings = useSelector(getTrackTextSettings)
  const isPhone: boolean = useSelector(
    state => state.app.mobileDeviceType === PHONE,
  )
  const theme = useContext(ThemeContext).theme
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const toggleExpandedPlayer = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded)
  }, [])
  const [isLyricsExpanded, setIsLyricsExpanded] = useState<boolean>(false)
  useEffect(() => {
    if (!currentTrack?.metadata?.['Lyrics']) {
      setIsLyricsExpanded(false)
    }
  }, [currentTrack])
  const isVideoFile: boolean =
    getFileTypeByExt(currentTrack?.ext) === VIDEO_FILE
  const isEnabled: boolean =
    Boolean(currentTrack) && (!isVideoFile || modalStyle !== 'overlay')
  const playlistId: PlaylistId | null | undefined = trackPlaylist?.playlist_id
  const trackIndex: number | null | undefined = Array.isArray(
    trackPlaylist?.tracks,
  )
    ? // @ts-expect-error
      trackPlaylist.tracks.findIndex(t => t === currentTrack?.id)
    : null
  const [isLyricsScrolled, setIsLyricsScrolled] = useState<boolean>(false)

  const handleLyricsScroll = event => {
    if (event.target.scrollTop > 0) {
      setIsLyricsScrolled(true)
    }

    if (event.target.scrollTop === 0) {
      setIsLyricsScrolled(false)
    }
  }

  const renderTrackInfo = (track: Track): React.ReactNode => {
    const { line1, line2 } = getTrackStatus(track, {
      textSettings,
    })
    return (
      <div className="Player-track">
        <div className="Player-track-artwork">
          <TrackArtwork track={track} />
        </div>
        <div className="Player-track-info">
          <div className="Player-track-title overflow-ellipsis">{line1}</div>
          <div className="Player-track-caption">{line2}</div>
        </div>
      </div>
    )
  }

  const expandedControls = currentTrack && (
    <>
      <div className="ExpandedPlayer-controls">
        <ButtonsWrapper>
          <PlayerPrev />
          <PlayerPlay
            mediaSource={mediaSource}
            playbackContext={{
              campaignId,
              groupId,
              paneIndex: playbackPane?.index ?? null,
              playlistId: playlistId,
              shortlistId: trackShortlist?.id,
              recipientId,
              shareId,
            }}
            trackId={currentTrack.id}
          />
          <PlayerNext />
        </ButtonsWrapper>
      </div>
      <div className="ExpandedPlayer-waveform">
        <ScrollableWaveform track={currentTrack} />
      </div>
    </>
  )
  let trackItemOptionsNode
  if (trackItemOptions && currentTrack)
    trackItemOptionsNode = (
      <TrackItemOptions
        showSaveToDisco={
          getSaveToDiscoVisibility(trackItemOptions.businessData) && // don't show Add to DISCO link in streaming only playlists
          (trackItemOptions.playlist.download_links ||
            (location === 'public-track' &&
              Boolean(currentTrack.public_download_url2)))
        }
        playlistId={trackItemOptions.playlist?.playlist_id}
        disableDownloads={
          !trackItemOptions.playlist?.download_links &&
          (location !== 'public-track' || !currentTrack.public_download_url2)
        }
        zipMode={trackItemOptions.playlist?.default_zip_mode}
        campaignId={campaignId}
        recipientId={recipientId}
        shareId={shareId}
        track={currentTrack}
        onOptionClick={toggleExpandedPlayer}
      />
    )
  const player = currentTrack && (
    <div className="Player">
      <MediaQuery
        maxWidth={600}
        onChange={isExpanded ? toggleExpandedPlayer : undefined}
      >
        <ThemeProvider
          value={{
            theme: 'dark',
          }}
        >
          <div className="Player-toolbar" onClick={toggleExpandedPlayer}>
            {renderTrackInfo(currentTrack)}
            <div onClick={stopPropagation}>
              <ButtonsWrapper>
                <PlayerPlay
                  mediaSource={mediaSource}
                  playbackContext={{
                    campaignId,
                    groupId,
                    paneIndex: playbackPane?.index ?? null,
                    playlistId: playlistId,
                    shortlistId: trackShortlist?.id,
                    recipientId,
                    shareId,
                  }}
                  trackId={currentTrack.id}
                />
                <PlayerNext />
              </ButtonsWrapper>
            </div>
          </div>
        </ThemeProvider>
        <PlayerPane open={isExpanded}>
          <ThemeProvider
            value={{
              theme: null,
            }}
          >
            <div className="ExpandedPlayer">
              <div className="ExpandedPlayer-actions">
                <div>
                  <Button
                    appearance="icon"
                    icon="arrow-down"
                    onClick={toggleExpandedPlayer}
                  />
                </div>
                {trackItemOptionsNode}
              </div>
              <div className="ExpandedPlayer-container">
                <div className="ExpandedPlayer-header">
                  <div className="ExpandedPlayer-title overflow-ellipsis">
                    {getTrackStatus(currentTrack).line1}
                  </div>
                  <div className="ExpandedPlayer-caption overflow-ellipsis">
                    {getTrackStatus(currentTrack).line2}
                  </div>
                </div>
                <div className="ExpandedPlayer-artwork">
                  <PlayerArtwork />
                </div>
                {expandedControls}
                {(trackItemOptions?.playlist.show_lyrics ||
                  location === 'public-track' ||
                  mediaSource === 'libraries') &&
                  currentTrack.metadata['Lyrics'] && (
                    <div
                      className="ExpandedPlayer-lyrics"
                      onClick={() => setIsLyricsExpanded(true)}
                    >
                      <Flex spacing="medium">
                        <Flex.Content>Lyrics</Flex.Content>
                        <div className="ExpandedPlayer-lyrics-button">
                          Show all
                        </div>
                      </Flex>
                    </div>
                  )}
              </div>
            </div>
          </ThemeProvider>
        </PlayerPane>
        {(trackItemOptions?.playlist.show_lyrics ||
          location === 'public-track' ||
          mediaSource === 'libraries') && (
          <PlayerPane open={isLyricsExpanded}>
            <ThemeProvider
              value={{
                theme: 'dark',
              }}
            >
              <div
                className={cn('ExpandedLyrics', {
                  'ExpandedLyrics--scrolled': isLyricsScrolled,
                })}
                onScroll={handleLyricsScroll}
              >
                <div className="ExpandedLyrics-container">
                  <div className="ExpandedLyrics-content">
                    <Stack>
                      <Flex spacing="small">
                        <Flex.Content>
                          {renderTrackInfo(currentTrack)}
                        </Flex.Content>
                        <div>
                          <Button
                            appearance="icon"
                            icon="cancel"
                            onClick={() => setIsLyricsExpanded(false)}
                          />
                        </div>
                      </Flex>
                      <div className="ExpandedLyrics-caption">Lyrics</div>
                      <div>{renderLyrics(currentTrack.metadata['Lyrics'])}</div>
                    </Stack>
                  </div>
                  <div className="ExpandedLyrics-controls">
                    {expandedControls}
                  </div>
                </div>
              </div>
            </ThemeProvider>
          </PlayerPane>
        )}
      </MediaQuery>
      <MediaQuery minWidth={601}>
        <div
          className={cn('Player-toolbar', {
            'Player-toolbar--public': isPublic || isPhone,
            'Player-toolbar--with-theme': Boolean(theme),
            'Player-toolbar--theme-dark': theme === 'dark',
            'Player-toolbar--theme-light': theme === 'light',
            'Player-toolbar--with-whitelabel-footer': !!whitelabelFooter,
          })}
        >
          {TrackActionsComponent && (
            <div className="Player-track-actions">
              <TrackActionsComponent
                track={currentTrack}
                groupId={groupId}
                sourcePlaylistId={playlistId}
                customEditMetadataUrl={
                  // TODO: this doesn't take into account shortlists
                  playlistId && trackIndex
                    ? `/explorer/playlists/${playlistId}/tracks/${trackIndex}/edit-metadata`
                    : null
                }
                allowSharing
                downloadTrack
                manageNesting
                manageTrackFormats
                editMetadata
                permissionsAndSharing
                addToShortlist
                deleteTrack
                addToCustomGroup
                addToNewCustomGroup
                isPlayer
              />
            </div>
          )}

          {renderTrackInfo(currentTrack)}

          <div className="Player-controls">
            <ButtonsWrapper>
              <PlayerPrev />
              <PlayerPlay
                mediaSource={mediaSource}
                playbackContext={{
                  campaignId,
                  groupId,
                  paneIndex: playbackPane?.index ?? null,
                  playlistId: playlistId,
                  shortlistId: trackShortlist?.id,
                  recipientId,
                  shareId,
                }}
                trackId={currentTrack.id}
              />
              <PlayerNext />
            </ButtonsWrapper>
          </div>

          <div className="Player-progress">
            <Flex spacing="medium">
              <div className="Player-progress-time">
                <PlaybackTime />
              </div>
              <Waveform
                className="Player-waveform"
                barGap={2}
                barWidth={1}
                noAnimation
                track={currentTrack}
                theme={theme}
              />
              <PlayTime time={currentTrack.duration} />
            </Flex>
          </div>

          <div
            className={cn('Player-actions', {
              'Player-actions--with-whitelabel-footer': !!whitelabelFooter,
            })}
          >
            <Flex spacing="small">
              <PlayerVolume />
              {!disabledQueue && (
                <PlayerQueue
                  mediaSource={mediaSource}
                  trackGroupId={groupId}
                  trackId={currentTrack.id}
                  trackPlaylistId={playlistId}
                  trackShortlistId={trackShortlist?.id}
                />
              )}
              {isPublic && !Boolean(theme) && (
                <div className="Player-logo">
                  <div
                    className="Player-logo--whitelabel"
                    dangerouslySetInnerHTML={{
                      __html: whitelabelFooter
                        ? sanitize(whitelabelFooter)
                        : undefined,
                    }}
                  />
                </div>
              )}
              {Boolean(theme) && trackItemOptions && (
                <>
                  <TrackDataInOneModal
                    track={currentTrack}
                    showLyrics={trackItemOptions.playlist.show_lyrics}
                    showMetadata={
                      !!trackItemOptions.businessData.public_metadata
                        .show_metadata_hover_on_public_pages &&
                      trackItemOptions.metadataSubset &&
                      Object.keys(currentTrack.metadata).some(key =>
                        trackItemOptions.metadataSubset?.includes(key),
                      )
                    }
                    metadataSubset={trackItemOptions.metadataSubset}
                  />
                  {trackItemOptionsNode}
                </>
              )}
            </Flex>
          </div>
        </div>
      </MediaQuery>
    </div>
  )
  return (
    <>
      {isEnabled && player}
      <PlayerPlaybackManager
        campaignId={campaignId}
        mediaSource={mediaSource}
        modalStyle={isVideoFile ? 'overlay' : modalStyle} // TODO: replace with playbackContext
        recipientId={recipientId}
        shareId={shareId}
        groupId={groupId}
        location={location}
        disableAutoplay={disableAutoplay}
      />
    </>
  )
}

export default React.memo<Props>(Player)
